import { router } from "../router/router.js";
import { contextStore } from "../auth/context-store.js";
import { platform } from "../../app/browser-platform.js";
import type { Professional } from "../../domain/identity/professional.js";
import { ProfessionalStatus } from "../../domain/identity/professional.js";
import type { Company } from "../../domain/identity/company.js";
import type { User } from "../../domain/identity/user.js";
import type { Client } from "../../domain/client/client.js";
import type { WorkOrder, SubWorkOrder } from "../../administration/admin-domain.js";

export default class AdminProfessionalsPage extends HTMLElement {
  private professionals: Professional[] = [];
  private companies: Company[] = [];
  private users: User[] = [];
  private clients: Client[] = [];
  private workOrders: WorkOrder[] = [];
  private subWorkOrders: SubWorkOrder[] = [];
  /** Vínculos com Projetos (Engenheiro responsável / PLH) — leitura; administrados em Gestão → Projetos. */
  private projectLinks: Array<{ professionalId: string; projectId: string; projectCode: string; clientName: string; asEngineer: boolean; asPlh: boolean }> = [];
  
  private searchQuery = "";
  private typeFilter = "ALL";
  private statusFilter = "ALL";
  private isModalOpen = false;
  private editingProfessional: Professional | null = null;
  private editingSignatureBase64: string | null = null;
  private originalSignatureBase64: string | null = null;
  private selectedSubOsIds: Set<string> = new Set();
  private modalSelectedClientId: string = "";
  private modalSelectedUserId: string = "";
  private deleteModal = { isOpen: false, id: "", title: "", message: "", error: "" };
  private boundHandleMasterDataUpdated: (e: Event) => void;

  constructor() {
    super();
    this.boundHandleMasterDataUpdated = this.handleMasterDataUpdated.bind(this);
  }

  private handleMasterDataUpdated(e: Event) {
    this.loadData();
  }

  public connectedCallback(): void {
    this.loadData();
    window.addEventListener("active-client-changed", () => this.loadData());
    window.addEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  public disconnectedCallback(): void {
    window.removeEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  private async loadData(): Promise<void> {
    try {
      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;
      const [profs, uList, clientList, woList, swoList, comps] = await Promise.all([
        platform.adminService.listProfessionals(dummyContext),
        platform.adminService.listUsers(dummyContext),
        platform.adminService.listClients(dummyContext),
        platform.adminService.listWorkOrders(dummyContext),
        platform.adminService.listSubWorkOrders(dummyContext),
        platform.adminService.listCompanies(dummyContext)
      ]);
      this.professionals = profs;
      this.users = uList;
      this.clients = clientList;
      this.workOrders = woList;
      this.subWorkOrders = swoList;
      this.companies = comps;
      try {
        const projects: any[] = await platform.projectService.listProjects(undefined, undefined, dummyContext);
        const clientName = (id: string) => { const c: any = clientList.find((x: any) => x.id === id); return c ? (c.fantasyName || c.name || c.legalName || id) : id; };
        // PLH vem do CLIENTE do Projeto (responsiblePlhProfessionalId), nunca
        // do campo legado Project.plhProfessionalId.
        const clientPlh = (id: string) => { const c: any = clientList.find((x: any) => x.id === id); return c?.responsiblePlhProfessionalId ? String(c.responsiblePlhProfessionalId) : ""; };
        this.projectLinks = [];
        for (const p of projects) {
          const plhOfClient = clientPlh(p.clientId);
          for (const pid of Array.from(new Set([p.engineerProfessionalId, plhOfClient].filter(Boolean)))) {
            this.projectLinks.push({ professionalId: String(pid), projectId: p.id, projectCode: p.code || p.id, clientName: clientName(p.clientId), asEngineer: String(p.engineerProfessionalId || "") === String(pid), asPlh: plhOfClient === String(pid) });
          }
        }
      } catch { this.projectLinks = []; }
      this.render();
    } catch (e) {
      console.error("Failed to load professionals", e);
    }
  }

  private render(): void {
    const filtered = this.professionals.filter(p => {
      const matchesStatus = this.statusFilter === "ALL" || p.status === this.statusFilter;
      const matchesType = this.typeFilter === "ALL" || p.professionalType === this.typeFilter;
      const term = this.searchQuery.toLowerCase();
      const matchesSearch = !term ||
        p.name.toLowerCase().includes(term) ||
        p.email.toLowerCase().includes(term) ||
        p.functionTitle.toLowerCase().includes(term) ||
        ((this.companies.find(c => c.id === p.companyId)?.tradeName || p.companyId || '') as string).toLowerCase().includes(term) ||
        (p.crea && p.crea.toLowerCase().includes(term)) ||
        (p.cft && p.cft.toLowerCase().includes(term)) ||
        (p.endIso && p.endIso.toLowerCase().includes(term)) ||
        (p.snqc && p.snqc.toLowerCase().includes(term));
      return matchesStatus && matchesType && matchesSearch;
    });

    const activeClientId = contextStore.get().activeClientId;

    this.innerHTML = `
      <div style="display: flex; flex-direction: column; height: 100%; gap: 16px; background: var(--color-bg-primary); padding: 24px;">
        <header style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 16px;">
          <div>
            <h1 style="color: var(--color-text-primary); font-size: 1.5rem; font-weight: 700; margin: 0;">Profissionais & Responsáveis Técnicos</h1>
            <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin-top: 4px;">Cadastro técnico de Engenheiros PLH, Inspetores e vinculação autorizada de Sub-OS por Cliente.</p>
            <p style="color: var(--color-graphite-400); font-size: 0.8rem; margin-top: 2px;">Os vínculos com Projetos são administrados em Gestão → Projetos.</p>
          </div>
          <button id="btn-new-prof" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 0.875rem; display: flex; align-items: center; gap: 6px;">
            <span>+ Novo Profissional</span>
          </button>
        </header>

        <!-- Filters -->
        <div style="display: flex; gap: 12px; background: var(--color-surface-primary); padding: 16px; border-radius: 8px; border: 1px solid var(--color-graphite-200); align-items: center; flex-wrap: wrap;">
          <input type="text" id="input-search" placeholder="Buscar por nome, e-mail, CREA, CFT, END ISO 9712 ou empresa..." value="${this.searchQuery}" style="flex: 1; min-width: 250px; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
          <select id="select-type" style="padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
            <option value="ALL" ${this.typeFilter === "ALL" ? "selected" : ""}>Todos os Tipos</option>
            <option value="ENGINEER" ${this.typeFilter === "ENGINEER" ? "selected" : ""}>Engenheiro PLH</option>
            <option value="INSPECTOR" ${this.typeFilter === "INSPECTOR" ? "selected" : ""}>Inspetor de Integridade</option>
            <option value="TECHNICIAN" ${this.typeFilter === "TECHNICIAN" ? "selected" : ""}>Técnico</option>
            <option value="SPECIALIST" ${this.typeFilter === "SPECIALIST" ? "selected" : ""}>Especialista</option>
          </select>
          <select id="select-status" style="padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
            <option value="ALL" ${this.statusFilter === "ALL" ? "selected" : ""}>Todos os Status</option>
            <option value="ACTIVE" ${this.statusFilter === "ACTIVE" ? "selected" : ""}>Ativos</option>
            <option value="INACTIVE" ${this.statusFilter === "INACTIVE" ? "selected" : ""}>Inativos</option>
          </select>
        </div>

        <!-- Data Table -->
        <div style="flex: 1; background: var(--color-surface-primary); border-radius: 8px; border: 1px solid var(--color-graphite-200); overflow-y: auto;">
          <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.875rem;">
            <thead style="background: var(--color-surface-secondary); border-bottom: 1px solid var(--color-graphite-200); position: sticky; top: 0;">
              <tr>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">NOME / E-MAIL</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">TIPO / FUNÇÃO</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">CLIENTE (PLH)</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">PROJETOS VINCULADOS</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">SUB-OS AUTORIZADAS</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">REGISTROS</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">STATUS</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600; text-align: right;">AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.length === 0 ? `
                <tr>
                  <td colspan="8" style="padding: 32px; text-align: center; color: var(--color-graphite-400);">Nenhum profissional cadastrado para esta unidade.</td>
                </tr>
              ` : filtered.map(p => {
                const userAccesses = this.subWorkOrders.filter(a => a.professionalId === p.id && a.status !== 'CANCELLED');
                const endIsoCode = p.endIso || p.endIso9712 || p.snqc;
                return `
                <tr style="border-bottom: 1px solid var(--color-bg-secondary);">
                  <td style="padding: 12px 16px;">
                    <div style="font-weight: 600; color: var(--ia-primary);">${p.name}</div>
                    <div style="color: var(--color-text-secondary); font-size: 0.8125rem;">${p.email}</div>
                  </td>
                  <td style="padding: 12px 16px;">
                    <span style="background: ${p.professionalType === 'ENGINEER' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(2, 132, 199, 0.1)'}; color: ${p.professionalType === 'ENGINEER' ? 'var(--color-primary)' : 'var(--color-ink)'}; padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-block; margin-bottom: 4px;">
                      ${p.professionalType === 'ENGINEER' ? 'ENGENHEIRO PLH' : p.professionalType === 'INSPECTOR' ? 'INSPETOR' : p.professionalType}
                    </span>
                    <div style="color: var(--color-text-secondary); font-size: 0.8125rem;">${p.functionTitle}</div>
                  </td>
                  <td style="padding: 12px 16px;">
                    ${p.professionalType === 'ENGINEER' ? (() => {
                      const plhClient = this.clients.find(c => c.id === p.plhClientId);
                      return plhClient
                        ? `<span style="background: rgba(99,102,241,0.08); color: var(--ia-primary); border: 1px solid rgba(99,102,241,0.25); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600;">${plhClient.name || plhClient.legalName}</span>`
                        : `<span style="color: var(--status-danger-solid); font-size: 0.8125rem; font-weight: 600;">⚠ Não vinculado</span>`;
                    })() : `<span style="color: var(--color-graphite-400); font-size: 0.8125rem;">-</span>`}
                  </td>
                  <td style="padding: 12px 16px;">
                    ${(() => {
                      const links = this.projectLinks.filter(l => String(l.professionalId) === String(p.id));
                      if (links.length === 0) return `<span style="color: var(--color-graphite-400); font-size: 0.8125rem;">Nenhum Projeto vinculado</span>`;
                      const clients = Array.from(new Set(links.map(l => l.clientName).filter(Boolean)));
                      return `<div style="display: flex; flex-direction: column; gap: 4px;" title="Os vínculos com Projetos são administrados em Gestão → Projetos.">
                        ${links.slice(0, 3).map(l => `<span style="font-size: 0.75rem; color: var(--color-text-primary);"><b>${l.projectCode}</b> ${l.asEngineer ? '<span style="background: rgba(99,102,241,0.12); color: var(--ia-primary); padding: 1px 6px; border-radius: 4px; font-weight: 700;">ENG. RESPONSÁVEL</span>' : ''} ${l.asPlh ? '<span style="background: rgba(124,58,237,0.15); color: #6d28d9; padding: 1px 6px; border-radius: 4px; font-weight: 700;">PLH</span>' : ''}</span>`).join('')}
                        ${links.length > 3 ? `<span style="font-size: 0.72rem; color: var(--color-text-secondary);">+${links.length - 3} projeto(s)</span>` : ''}
                        <span style="font-size: 0.72rem; color: var(--color-text-secondary);">Clientes: ${clients.join(', ') || '—'}</span>
                        <span style="font-size: 0.68rem; color: var(--color-graphite-400);">Somente leitura — Gestão → Projetos</span>
                      </div>`;
                    })()}
                  </td>
                  <td style="padding: 12px 16px;">
                    ${userAccesses.length === 0 ? `
                      <span style="color: var(--color-graphite-400); font-size: 0.8125rem;">Nenhuma Sub-OS vinculada</span>
                    ` : `
                      <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                        ${userAccesses.slice(0, 3).map(a => `
                          <span style="background: var(--color-surface-secondary); color: var(--ia-primary); border: 1px solid var(--color-border-subtle); padding: 2px 6px; border-radius: 4px; font-size: 0.75rem; font-weight: 600;">
                            ${a.code || a.id}
                          </span>
                        `).join('')}
                        ${userAccesses.length > 3 ? `
                          <span style="background: var(--color-surface-secondary); color: var(--color-text-secondary); padding: 2px 6px; border-radius: 4px; font-size: 0.75rem; font-weight: 600;">
                            +${userAccesses.length - 3}
                          </span>
                        ` : ''}
                      </div>
                    `}
                  </td>
                  <td style="padding: 12px 16px; font-family: monospace; font-size: 0.8125rem; color: var(--color-text-secondary);">
                    ${p.crea ? `<div>CREA: <b>${p.crea}</b></div>` : ''}
                    ${p.cft ? `<div>CFT: <b>${p.cft}</b></div>` : ''}
                    ${endIsoCode ? `<div>END ISO 9712: <b>${endIsoCode}</b></div>` : ''}
                    ${!p.crea && !p.cft && !endIsoCode ? '<span style="color: var(--color-graphite-400);">Nenhum registro</span>' : ''}
                  </td>
                  <td style="padding: 12px 16px;">
                    <span style="background: ${p.status === 'ACTIVE' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'}; color: ${p.status === 'ACTIVE' ? 'var(--status-success-solid)' : 'var(--status-danger-solid)'}; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">
                      ${p.status === 'ACTIVE' ? 'ATIVO' : 'INATIVO'}
                    </span>
                  </td>
                  <td style="padding: 12px 16px; text-align: right;">
                    <button class="btn-edit" data-id="${p.id}" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--color-text-muted); margin-right: 4px;">Editar</button>
                    ${p.status === 'ACTIVE' ? `
                      <button class="btn-inactivate" data-id="${p.id}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text); margin-right: 4px;">Inativar</button>
                    ` : ''}
                    <button class="btn-delete" data-id="${p.id}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-solid); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text);">Excluir</button>
                  </td>
                </tr>
              `;}).join("")}
            </tbody>
          </table>
        </div>

        ${this.isModalOpen ? this.renderModal() : ""}
        ${this.deleteModal.isOpen ? this.renderDeleteModal() : ""}
      </div>
    `;

    this.attachEvents();
  }

  private renderModal(): string {
    const isEdit = !!this.editingProfessional;
    const activeClientId = contextStore.get().activeClientId;
    const selectedClientId = this.modalSelectedClientId || activeClientId;

    // Filter available users (status ACTIVE and not linked to any active professional)
    const activeProfessionals = this.professionals.filter(p => p.status === 'ACTIVE');
    const linkedUserIds = new Set(activeProfessionals.map(p => p.userId).filter(Boolean));
    const availableUsers = this.users.filter(u => u.status === 'ACTIVE' && !linkedUserIds.has(u.id));

    const linkedUser = isEdit ? this.users.find(u => u.id === this.editingProfessional?.userId) : null;
    const selectedNewUser = !isEdit && this.modalSelectedUserId ? availableUsers.find(u => u.id === this.modalSelectedUserId) : null;

    if (!isEdit && availableUsers.length === 0) {
      return `
        <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
          <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 500px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); text-align: center;">
            <div style="font-size: 3rem; margin-bottom: 16px;"><i data-lucide="user" style="width: 3rem; height: 3rem;"></i></div>
            <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0 0 8px 0;">Nenhum Usuário Disponível</h2>
            <p style="color: var(--color-text-secondary); font-size: 0.95rem; margin-bottom: 24px;">Não há usuários disponíveis para vinculação. Cadastre primeiro um usuário no sistema ou inative profissionais antigos.</p>
            <button id="btn-close-modal" style="background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; padding: 10px 20px; font-size: 0.875rem; font-weight: 600; color: var(--ia-action-fg, #F7F8F8); cursor: pointer;">Fechar</button>
          </div>
        </div>
      `;
    }

    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px); padding: 16px; box-sizing: border-box;">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 800px; max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); overflow: hidden;">
          
          <!-- HEADER -->
          <div style="padding: 20px 24px; border-bottom: 1px solid var(--color-graphite-200); display: flex; justify-content: space-between; align-items: center; background: var(--color-surface-primary); z-index: 10; flex-shrink: 0;">
            <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0;">${isEdit ? 'Editar Profissional' : 'Novo Profissional'}</h2>
            <button id="btn-close-modal" type="button" style="background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary); padding: 0;">✕</button>
          </div>

          <!-- BODY -->
          <div style="padding: 24px; overflow-y: auto; flex: 1;">
            <div id="modal-error-banner" style="display: none; background: var(--status-danger-bg); color: var(--status-danger-text); padding: 12px; border-radius: 6px; border: 1px solid var(--status-danger-bg); margin-bottom: 16px; font-size: 0.875rem; font-weight: 500;"></div>
            
            <form id="form-prof" style="display: flex; flex-direction: column; gap: 24px;">
              
              <!-- SEÇÃO 1: USUÁRIO -->
              <div>
                <h3 style="font-size: 0.875rem; font-weight: 700; color: var(--color-text-muted); margin: 0 0 12px 0; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 8px;">1. USUÁRIO VINCULADO</h3>
                
                ${isEdit ? `
                  <div style="background: var(--color-bg-primary); padding: 16px; border-radius: 8px; border: 1px solid var(--color-graphite-200); display: flex; gap: 16px; align-items: center;">
                    <div style="width: 48px; height: 48px; border-radius: 24px; background: var(--color-surface-secondary); color: var(--color-ink); display: flex; align-items: center; justify-content: center; font-size: 1.25rem; font-weight: 700;">
                      ${linkedUser ? linkedUser.name.substring(0, 2).toUpperCase() : (this.editingProfessional?.name || '?').substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div style="font-size: 0.75rem; font-weight: 700; color: var(--color-text-secondary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">DADOS DO USUÁRIO VINCULADO</div>
                      <div style="font-weight: 600; color: var(--color-text-muted); font-size: 1rem;">${linkedUser ? linkedUser.name : this.editingProfessional?.name}</div>
                      <div style="color: var(--color-text-secondary); font-size: 0.875rem;">${linkedUser ? linkedUser.email : this.editingProfessional?.email}</div>
                      <div style="color: var(--color-text-secondary); font-size: 0.8125rem;">Status: <span style="font-weight: 600; color: var(--status-success-text);">Ativo</span></div>
                    </div>
                  </div>
                ` : `
                  <div style="display: flex; flex-direction: column; gap: 12px;">
                    <div>
                      <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Selecione o Usuário *</label>
                      <select id="modal-userId" required style="width: 100%; padding: 10px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.95rem; box-sizing: border-box; background: var(--color-surface-primary); color: var(--color-text-muted);">
                        <option value="">Selecione um usuário...</option>
                        ${availableUsers.map(u => `<option value="${u.id}" ${this.modalSelectedUserId === u.id ? 'selected' : ''}>${u.name} (${u.email})</option>`).join('')}
                      </select>
                    </div>

                    ${selectedNewUser ? `
                      <div style="background: var(--color-bg-primary); padding: 16px; border-radius: 8px; border: 1px solid var(--color-graphite-200); display: flex; gap: 16px; align-items: center; margin-top: 8px;">
                        <div style="width: 48px; height: 48px; border-radius: 24px; background: var(--color-surface-secondary); color: var(--color-ink); display: flex; align-items: center; justify-content: center; font-size: 1.25rem; font-weight: 700;">
                          ${selectedNewUser.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div style="flex: 1;">
                          <div style="font-weight: 600; color: var(--color-text-muted); font-size: 1rem;">${selectedNewUser.name}</div>
                          <div style="color: var(--color-text-secondary); font-size: 0.875rem;">${selectedNewUser.email}</div>
                          <div style="color: var(--color-text-secondary); font-size: 0.8125rem;">Status: <span style="font-weight: 600; color: var(--status-success-text);">Ativo</span></div>
                        </div>
                      </div>
                    ` : `
                      <div style="background: var(--color-bg-primary); padding: 16px; border-radius: 8px; border: 1px dashed var(--color-graphite-300); text-align: center; color: var(--color-text-secondary); font-size: 0.875rem;">
                        Selecione um usuário acima para continuar.
                      </div>
                    `}
                  </div>
                `}
              </div>

              <!-- SEÇÃO 2 & 3: TIPO E CARGO -->
              <div>
                <h3 style="font-size: 0.875rem; font-weight: 700; color: var(--color-text-muted); margin: 0 0 12px 0; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 8px;">2. DADOS PROFISSIONAIS & VÍNCULO</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 16px;">
                  <div>
                    <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Tipo de Profissional *</label>
                    <select id="modal-type" required ${!isEdit && !selectedNewUser ? 'disabled' : ''} style="width: 100%; padding: 10px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: var(--color-surface-primary);">
                      <option value="ENGINEER" ${this.editingProfessional?.professionalType === 'ENGINEER' ? 'selected' : ''}>Engenheiro PLH (Habilitado)</option>
                      <option value="INSPECTOR" ${this.editingProfessional?.professionalType === 'INSPECTOR' || !this.editingProfessional ? 'selected' : ''}>Inspetor de Integridade</option>
                      <option value="TECHNICIAN" ${this.editingProfessional?.professionalType === 'TECHNICIAN' ? 'selected' : ''}>Técnico</option>
                      <option value="SPECIALIST" ${this.editingProfessional?.professionalType === 'SPECIALIST' ? 'selected' : ''}>Especialista</option>
                    </select>
                  </div>
                  <div>
                    <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Cargo / Função *</label>
                    <input type="text" id="modal-function" value="${this.editingProfessional?.functionTitle || ''}" required ${!isEdit && !selectedNewUser ? 'disabled' : ''} placeholder="Ex: Inspetor N2, Eng. Mecânico" style="width: 100%; padding: 10px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
                  </div>
                  <div>
                    <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Empresa Prestadora (PJ)</label>
                    <select id="modal-companyId" ${!isEdit && !selectedNewUser ? 'disabled' : ''} style="width: 100%; padding: 10px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: var(--color-surface-primary);">
                      <option value="">(Empresa Própria / Quadro Interno)</option>
                      ${this.companies.map(c => `
                        <option value="${c.id}" ${this.editingProfessional?.companyId === c.id ? 'selected' : ''}>${c.tradeName} (CNPJ: ${c.cnpj})</option>
                      `).join("")}
                    </select>
                  </div>
                </div>

                <!-- CAMPO CLIENTE PLH (condicional - aparece apenas para ENGENHEIRO) -->
                <div id="section-plh-client" style="margin-top: 16px; display: ${this.editingProfessional?.professionalType === 'ENGINEER' || !this.editingProfessional ? 'block' : 'none'};">
                  <div style="background: var(--status-info-bg); border: 1px solid var(--color-border-subtle); border-radius: 8px; padding: 16px;">
                    <label style="display: block; font-size: 0.8125rem; font-weight: 700; color: var(--ia-primary); margin-bottom: 8px;">
                      <i data-lucide="building-2" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> Cliente de habilitação (opcional)
                      <span style="font-weight: 400; color: var(--ia-primary); font-size: 0.75rem;"> — campo legado, não define responsabilidade</span>
                    </label>
                    <select id="modal-plhClientId" style="width: 100%; padding: 10px 12px; border: 2px solid var(--color-border-default); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: var(--color-surface-primary); color: var(--ia-primary);" ${!isEdit && !selectedNewUser ? 'disabled' : ''}>
                      <option value="">Nenhum — vincular depois em Clientes</option>
                      ${this.clients.map(c => `
                        <option value="${c.id}" ${this.editingProfessional?.plhClientId === c.id ? 'selected' : ''}>${c.name || c.legalName}</option>
                      `).join("")}
                    </select>
                    <!-- O texto anterior ("só aparecerá como responsável em
                         projetos do cliente selecionado") contradizia a regra
                         oficial: a responsabilidade é do CLIENTE, um mesmo
                         Engenheiro pode ser PLH de vários, e o campo marcado
                         com "*" fazia parecer que o cadastro dependia disso. -->
                    <p style="font-size: 0.75rem; color: var(--ia-primary); margin: 8px 0 0 0;">
                      O Engenheiro/PLH é cadastrado <strong>sem Cliente</strong>. A responsabilidade técnica é atribuída depois, em
                      <strong>Administração → Clientes → PLH Responsável</strong> — e o mesmo Engenheiro pode ser PLH de vários Clientes.
                    </p>
                  </div>
                </div>
              </div>

              <!-- SEÇÃO 3: REGISTROS PROFISSIONAIS -->
              <div>
                <h3 style="font-size: 0.875rem; font-weight: 700; color: var(--color-text-muted); margin: 0 0 12px 0; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 8px;">3. REGISTROS PROFISSIONAIS</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px;">
                  <div>
                    <label style="display: block; font-size: 0.75rem; font-weight: 600; color: var(--color-text-secondary); margin-bottom: 4px;">CREA (Engenharia)</label>
                    <input type="text" id="modal-crea" value="${this.editingProfessional?.crea || ''}" ${!isEdit && !selectedNewUser ? 'disabled' : ''} placeholder="Ex: CREA-SP 123456/D" style="width: 100%; padding: 10px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
                  </div>
                  <div>
                    <label style="display: block; font-size: 0.75rem; font-weight: 600; color: var(--color-text-secondary); margin-bottom: 4px;">CFT (Técnico)</label>
                    <input type="text" id="modal-cft" value="${this.editingProfessional?.cft || ''}" ${!isEdit && !selectedNewUser ? 'disabled' : ''} placeholder="Ex: CFT-BR 987654" style="width: 100%; padding: 10px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
                  </div>
                  <div>
                    <label style="display: block; font-size: 0.75rem; font-weight: 600; color: var(--color-text-secondary); margin-bottom: 4px;">END ISO 9712 (Qualificação END)</label>
                    <input type="text" id="modal-end-iso" value="${this.editingProfessional?.endIso || this.editingProfessional?.endIso9712 || this.editingProfessional?.snqc || ''}" ${!isEdit && !selectedNewUser ? 'disabled' : ''} placeholder="Ex: ISO 9712-12345" style="width: 100%; padding: 10px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
                  </div>
                </div>
              </div>
              
              <!-- SEÇÃO 4: ASSINATURA DO PROFISSIONAL -->
              <div>
                <h3 style="font-size: 0.875rem; font-weight: 700; color: var(--color-text-muted); margin: 0 0 12px 0; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 8px;">4. ASSINATURA DO PROFISSIONAL</h3>
                
                <div style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-300); border-radius: 8px; padding: 16px; display: flex; flex-direction: column; gap: 16px; align-items: flex-start;">
                  <div style="font-size: 0.8125rem; color: var(--color-text-secondary); width: 100%;">
                    Faça o upload da assinatura para constar automaticamente nos relatórios. (PNG, JPG, WEBP - Max: 5MB)
                  </div>
                  
                  ${this.editingSignatureBase64 ? `
                    <div style="display: flex; gap: 24px; align-items: center; background: var(--color-bg-secondary); padding: 16px; border-radius: 6px; width: 100%; box-sizing: border-box; border: 1px solid var(--color-graphite-200);">
                      <div style="width: 200px; height: 100px; display: flex; align-items: center; justify-content: center; background: #fff; border: 1px dashed var(--color-graphite-300); border-radius: 4px; overflow: hidden; padding: 8px;">
                        <img src="${this.editingSignatureBase64}" style="max-width: 100%; max-height: 100%; object-fit: contain;" alt="Assinatura" />
                      </div>
                      <div style="display: flex; flex-direction: column; gap: 8px;">
                        <button type="button" id="btn-replace-signature" style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 6px 12px; font-size: 0.75rem; font-weight: 600; cursor: pointer; color: var(--color-text-primary);">Substituir Assinatura</button>
                        <button type="button" id="btn-remove-signature" style="background: var(--status-danger-bg); border: none; border-radius: 6px; padding: 6px 12px; font-size: 0.75rem; font-weight: 600; cursor: pointer; color: var(--status-danger-text);">Remover Assinatura</button>
                      </div>
                    </div>
                  ` : `
                    <label style="display: flex; flex-direction: column; align-items: center; justify-content: center; width: 100%; padding: 32px 16px; border: 2px dashed var(--color-graphite-300); border-radius: 8px; background: var(--color-bg-primary); cursor: pointer; box-sizing: border-box;">
                      <i data-lucide="upload-cloud" style="width: 24px; height: 24px; color: var(--color-text-muted); margin-bottom: 8px;"></i>
                      <span style="font-size: 0.875rem; font-weight: 600; color: var(--color-ink);">Clique para anexar arquivo</span>
                      <span style="font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 4px;">Somente imagens. Máx 5MB.</span>
                      <input type="file" id="modal-signature-input" accept="image/png, image/jpeg, image/webp" style="display: none;" />
                    </label>
                  `}
                </div>
              </div>

            </form>
          </div>

          <!-- FOOTER -->
          <div style="padding: 16px 24px; border-top: 1px solid var(--color-graphite-200); background: var(--color-bg-primary); display: flex; justify-content: flex-end; gap: 12px; z-index: 10; flex-shrink: 0;">
            <button type="button" id="btn-cancel-modal" style="background: var(--color-surface-primary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 10px 20px; font-size: 0.875rem; font-weight: 600; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
            <button type="submit" form="form-prof" ${!isEdit && !selectedNewUser ? 'disabled' : ''} style="background: ${!isEdit && !selectedNewUser ? 'var(--color-graphite-400)' : 'var(--color-ink)'}; border: none; border-radius: 6px; padding: 10px 20px; font-size: 0.875rem; font-weight: 600; color: var(--color-text-primary); cursor: ${!isEdit && !selectedNewUser ? 'not-allowed' : 'pointer'};">Salvar</button>
          </div>
        </div>
      </div>
    `;
  }

  private attachEvents(): void {
    this.querySelector("#input-search")?.addEventListener("input", (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.render();
    });

    this.querySelector("#select-type")?.addEventListener("change", (e) => {
      this.typeFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector("#select-status")?.addEventListener("change", (e) => {
      this.statusFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector("#btn-new-prof")?.addEventListener("click", () => {
      this.editingProfessional = null;
      this.editingSignatureBase64 = null;
      this.originalSignatureBase64 = null;
      this.selectedSubOsIds = new Set();
      this.modalSelectedClientId = contextStore.get().activeClientId || "";
      this.modalSelectedUserId = "";
      this.isModalOpen = true;
      this.render();
    });

    this.querySelectorAll(".btn-edit").forEach(btn => {
      btn.addEventListener("click", async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        this.editingProfessional = this.professionals.find(p => p.id === id) || null;
        this.modalSelectedClientId = contextStore.get().activeClientId || "";
        this.modalSelectedUserId = "";
        
        try {
          const ctx = contextStore.get();
          const dummyContext = { activeClientId: ctx.activeClientId, permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
          this.editingSignatureBase64 = await platform.adminService.getProfessionalSignature(dummyContext, id || "") || null;
          this.originalSignatureBase64 = this.editingSignatureBase64;
        } catch {
          this.editingSignatureBase64 = null;
          this.originalSignatureBase64 = null;
        }
        
        this.isModalOpen = true;
        this.render();
      });
    });

    this.querySelectorAll(".btn-inactivate").forEach(btn => {
      btn.addEventListener("click", async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        if (!id) return;
        this.deleteModal = {
          isOpen: true,
          id,
          title: "Inativar Profissional",
          message: "Deseja realmente inativar o acesso deste profissional?",
          error: ""
        };
        this.render();
      });
    });

    this.querySelectorAll(".btn-delete").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        if (!id) return;
        this.deleteModal = {
          isOpen: true,
          id,
          title: "Excluir Profissional",
          message: "Atenção: A exclusão requer autorização administrativa. Deseja prosseguir?",
          error: ""
        };
        this.render();
      });
    });

    this.querySelector("#btn-close-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });

    this.querySelector("#btn-cancel-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });
    
    this.querySelector("#modal-userId")?.addEventListener("change", (e) => {
      this.modalSelectedUserId = (e.target as HTMLSelectElement).value;
      this.render();
    });

    // Mostrar/ocultar campo "Cliente PLH" dinamicamente conforme tipo selecionado
    this.querySelector("#modal-type")?.addEventListener("change", (e) => {
      const type = (e.target as HTMLSelectElement).value;
      const plhSection = this.querySelector("#section-plh-client") as HTMLElement;
      if (plhSection) {
        plhSection.style.display = type === 'ENGINEER' ? 'block' : 'none';
      }
    });

    const signatureInput = this.querySelector("#modal-signature-input") as HTMLInputElement;
    if (signatureInput) {
      signatureInput.addEventListener("change", (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          if (file.size > 5 * 1024 * 1024) {
            alert("A imagem deve ter no máximo 5MB.");
            return;
          }
          const reader = new FileReader();
          reader.onload = (ev) => {
            this.editingSignatureBase64 = ev.target?.result as string;
            this.render();
          };
          reader.readAsDataURL(file);
        }
      });
    }

    this.querySelector("#btn-remove-signature")?.addEventListener("click", () => {
      if (confirm("Deseja realmente remover esta assinatura? (Será efetivado ao salvar)")) {
        this.editingSignatureBase64 = null;
        this.render();
      }
    });

    this.querySelector("#btn-replace-signature")?.addEventListener("click", () => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/png, image/jpeg, image/webp";
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          if (file.size > 5 * 1024 * 1024) {
            alert("A imagem deve ter no máximo 5MB.");
            return;
          }
          const reader = new FileReader();
          reader.onload = (ev) => {
            this.editingSignatureBase64 = ev.target?.result as string;
            this.render();
          };
          reader.readAsDataURL(file);
        }
      };
      input.click();
    });

    this.querySelector("#form-prof")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      
      const userId = (this.querySelector("#modal-userId") as HTMLSelectElement)?.value || null;
      if (!this.editingProfessional && !userId) {
        return; // blocked by UI already, double check
      }
      
      const professionalType = (this.querySelector("#modal-type") as HTMLSelectElement).value;
      const functionTitle = (this.querySelector("#modal-function") as HTMLInputElement).value;
      const companyId = (this.querySelector("#modal-companyId") as HTMLSelectElement)?.value || null;
      const plhClientId = (this.querySelector("#modal-plhClientId") as HTMLSelectElement)?.value || null;
      const crea = (this.querySelector("#modal-crea") as HTMLInputElement).value || null;
      const cft = (this.querySelector("#modal-cft") as HTMLInputElement).value || null;
      const endIso = (this.querySelector("#modal-end-iso") as HTMLInputElement)?.value || null;

      const ctx = contextStore.get();
      const activeClientId = ctx.activeClientId;
      const dummyContext = {
        activeClientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      const submitBtn = this.querySelector('button[type="submit"]') as HTMLButtonElement;
      const originalText = submitBtn.textContent;
      submitBtn.textContent = "Salvando...";
      submitBtn.disabled = true;

      const errorBanner = this.querySelector("#modal-error-banner") as HTMLElement;
      if (errorBanner) {
        errorBanner.style.display = "none";
      }

      try {
        let resultProfessional: Professional | null = null;
        if (this.editingProfessional) {
          resultProfessional = await platform.adminService.updateProfessional(dummyContext, {
            ...this.editingProfessional,
            professionalType,
            functionTitle,
            companyId,
            plhClientId,
            crea,
            cft,
            endIso,
            endIso9712: endIso,
            snqc: endIso,
            sncc: null
          });
        } else {
          resultProfessional = await platform.adminService.createProfessional(dummyContext, {
            name: "", // Herda do User
            email: "", // Herda do User
            userId,
            professionalType,
            functionTitle,
            companyId,
            plhClientId,
            crea,
            cft,
            endIso,
            endIso9712: endIso,
            snqc: endIso,
            sncc: null
          });
        }

        if (resultProfessional) {
          if (this.editingSignatureBase64 !== this.originalSignatureBase64) {
            if (this.editingSignatureBase64 === null) {
              await platform.adminService.removeProfessionalSignature(dummyContext, resultProfessional.id);
            } else {
              await platform.adminService.uploadProfessionalSignature(dummyContext, resultProfessional.id, this.editingSignatureBase64);
            }
          }
        }

        this.isModalOpen = false;
        await this.loadData();
      } catch (err: any) {
        if (submitBtn) {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }
        if (errorBanner) {
          errorBanner.textContent = err.message || "Erro ao salvar profissional.";
          errorBanner.style.display = "block";
        } else {
          console.error(err);
        }
      }
    });

    this.querySelector("#form-delete")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const { id, title } = this.deleteModal;
      const isInactivate = title.includes("Inativar");
      
      const passwordAttempt = (this.querySelector("#modal-delete-password") as HTMLInputElement)?.value || "";
      const reason = (this.querySelector("#modal-delete-reason") as HTMLInputElement)?.value || "";

      const ctx = contextStore.get();
      // Quem executa a ação é quem está autenticado. Não há queda para
      // 'usr_admin' nem leitura de `inspect_user_id` do navegador: sem
      // sessão, a ação não é atribuída a ninguém e o serviço a recusa.
      const dummyContext = {
        activeClientId: ctx.activeClientId || "",
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      try {
        if (isInactivate) {
          await platform.adminService.inactivateProfessional(dummyContext, id);
          this.deleteModal.isOpen = false;
          await this.loadData();
        } else {
          if ((platform.adminService as any).requestProtectedDeletion) {
             const res = await (platform.adminService as any).requestProtectedDeletion(dummyContext, "PROFESSIONAL", id, passwordAttempt, reason);
             if (res.success) {
               this.deleteModal.isOpen = false;
               await this.loadData();
             } else {
               this.deleteModal.error = res.message || "Erro na exclusão protegida.";
               this.render();
             }
          } else {
             await (platform.adminService as any).deleteProfessional?.(dummyContext, id);
             this.deleteModal.isOpen = false;
             await this.loadData();
          }
        }
      } catch (e: any) {
        this.deleteModal.error = e.message || "Erro ao executar ação.";
        this.render();
      }
    });

    this.querySelector("#btn-cancel-delete")?.addEventListener("click", () => {
      this.deleteModal.isOpen = false;
      this.render();
    });
  }

  private renderDeleteModal(): string {
    const { title, message, error } = this.deleteModal;
    const isInactivate = title.includes("Inativar");
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 450px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); text-align: left;">
          <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0 0 16px 0; text-align: center;">${title}</h2>
          <p style="color: var(--color-text-secondary); font-size: 0.95rem; margin-bottom: 24px; text-align: center;">${message}</p>
          ${error ? `<div style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 6px; padding: 10px 14px; font-size: 0.8125rem; color: var(--status-danger-text); margin-bottom: 16px;">${error}</div>` : ''}
          
          <form id="form-delete" style="display: flex; flex-direction: column; gap: 16px;">
            ${!isInactivate ? `
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Senha do Administrador *</label>
              <input type="password" id="modal-delete-password" required placeholder="Digite sua senha atual" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Motivo *</label>
              <input type="text" id="modal-delete-reason" required placeholder="Motivo da exclusão" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>
            ` : ''}
            
            <div style="display: flex; justify-content: center; gap: 12px; margin-top: 8px;">
              <button type="button" id="btn-cancel-delete" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 24px; font-size: 0.875rem; font-weight: 600; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
              <button type="submit" style="background: var(--status-danger-solid); border: none; border-radius: 6px; padding: 8px 24px; font-size: 0.875rem; font-weight: 600; color: var(--color-text-primary); cursor: pointer;">Confirmar</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }
}

if (!customElements.get("app-admin-professionals-page")) {
  customElements.define("app-admin-professionals-page", AdminProfessionalsPage);
}




